import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CrmToaster, IconNote, Pill, crmToast } from './crmUi'
import { Button } from '../shared/Button'
import { parseLeadImport, getImportExistingEmails, commitLeadImport, type LeadParse, type LeadImportResult } from './crmApi'
import {
  UNIFIED_GROUPS,
  REQUIRED_FIELDS,
  TO_NOTES,
  IGNORE,
  FIRSTNAME,
  LASTNAME,
  autoMapHeaders,
  applyMappingJson,
  buildUnifiedRecord,
  validateRecord,
  type Mapping,
  type RowIssue,
} from './leadSchema'

/**
 * WeQuote lead-import & column-mapping tool (/crm/leads/import). Works on a raw
 * event sheet on its own, or on a skill-standardised sheet (+ mapping.json).
 *
 * Phase 1: upload → parse + capture Opportunity / Date / Owner.
 * Phase 2: auto-mapping grid — each source column → a unified field / → Notes /
 * Ignore, First+Last auto-combine into Full Name, unmapped → Notes, optional
 * mapping.json pre-fill. (Review + commit follow in Phases 3–4.)
 */

export interface ImportMeta {
  opportunity: string
  capturedDate: string
  owner: string
  leadSource: string
}

type RowStatus = 'valid' | 'warn' | 'error' | 'duplicate' | 'excluded'
interface ReviewRow {
  i: number
  rec: Record<string, string>
  issues: RowIssue[]
  duplicate: boolean
  status: RowStatus
}
const STATUS_META: Record<RowStatus, { label: string; tone: 'green' | 'amber' | 'red' | 'blue' | 'gray' }> = {
  valid: { label: 'Valid', tone: 'green' },
  warn: { label: 'Check', tone: 'amber' },
  error: { label: 'Review', tone: 'red' },
  duplicate: { label: 'Duplicate', tone: 'blue' },
  excluded: { label: 'Excluded', tone: 'gray' },
}

const STEPS = ['Upload', 'Map columns', 'Review', 'Import']
const fileBase = (name: string) => name.replace(/\.[^.]+$/, '')

const Stepper: React.FC<{ step: number }> = ({ step }) => (
  <div className="flex items-center gap-2">
    {STEPS.map((label, i) => {
      const state = i < step ? 'done' : i === step ? 'active' : 'todo'
      return (
        <React.Fragment key={label}>
          <div className="flex items-center gap-2">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${state === 'active' ? 'bg-brand-600 text-white' : state === 'done' ? 'bg-green-600 text-white' : 'bg-surface-tertiary text-txt-tertiary'}`}>
              {state === 'done' ? '✓' : i + 1}
            </span>
            <span className={`text-xs font-medium ${state === 'todo' ? 'text-txt-tertiary' : 'text-txt-primary'} hidden sm:inline`}>{label}</span>
          </div>
          {i < STEPS.length - 1 && <span className="w-5 h-px bg-border" />}
        </React.Fragment>
      )
    })}
  </div>
)

const field = 'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-txt-primary placeholder:text-txt-tertiary focus:outline-none focus:border-brand-400'

const CountTile: React.FC<{ label: string; value: number; tone?: 'green' | 'amber' | 'blue' | 'default' }> = ({ label, value, tone = 'default' }) => {
  const c = { default: 'text-txt-primary', green: 'text-success', amber: 'text-amber-600 dark:text-amber-400', blue: 'text-brand-600' }[tone]
  return (
    <div className="rounded-xl border border-border bg-surface p-3">
      <p className="text-[11px] font-medium uppercase tracking-wide text-txt-tertiary">{label}</p>
      <p className={`text-2xl font-bold mt-0.5 ${c}`}>{value}</p>
    </div>
  )
}

/** Dropdown to pick a unified field (or Notes/Ignore/combine) for one column. */
const MapSelect: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className={field}>
    <option value={TO_NOTES}>→ Notes (keep, don't map)</option>
    <option value={IGNORE}>Ignore this column</option>
    <optgroup label="Combine → Full Name">
      <option value={FIRSTNAME}>First name → Full Name</option>
      <option value={LASTNAME}>Last name → Full Name</option>
    </optgroup>
    {UNIFIED_GROUPS.map((g) => (
      <optgroup key={g.name} label={g.name}>
        {g.fields.map((f) => (
          <option key={f} value={f}>
            {f}
            {REQUIRED_FIELDS.includes(f) ? ' ★' : ''}
          </option>
        ))}
      </optgroup>
    ))}
  </select>
)

export const CrmLeadImport: React.FC = () => {
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const inputRef = useRef<HTMLInputElement>(null)
  const mapJsonRef = useRef<HTMLInputElement>(null)

  const [file, setFile] = useState<File | null>(null)
  const [parsed, setParsed] = useState<LeadParse | null>(null)
  const [mapping, setMapping] = useState<Mapping>({})
  const [busy, setBusy] = useState(false)
  const [step, setStep] = useState(0)
  const fromSkill = search.get('src') === 'skill' || search.has('opportunity')
  const [meta, setMeta] = useState<ImportMeta>({
    opportunity: search.get('opportunity') || '',
    capturedDate: search.get('date') || search.get('capturedDate') || '',
    owner: search.get('owner') || '',
    leadSource: search.get('leadSource') || search.get('source') || 'Trade Show',
  })
  const [existingEmails, setExistingEmails] = useState<Set<string>>(new Set())
  const [edits, setEdits] = useState<Record<number, Record<string, string>>>({})
  const [excluded, setExcluded] = useState<Set<number>>(new Set())

  // Fetch known emails once we reach the review step (for dedupe).
  useEffect(() => {
    if (step === 2 && existingEmails.size === 0) {
      getImportExistingEmails().then((e) => setExistingEmails(new Set(e.map((x) => x.toLowerCase())))).catch(() => {})
    }
  }, [step, existingEmails.size])

  // Derive the mapped + validated + deduped review rows.
  const review = useMemo(() => {
    if (!parsed) return { rows: [] as ReviewRow[], counts: { valid: 0, flagged: 0, duplicate: 0, excluded: 0, importable: 0 } }
    const seen = new Set<string>()
    const rows: ReviewRow[] = parsed.rows.map((row, i) => {
      const base = buildUnifiedRecord(parsed.headers, row, mapping, { opportunity: meta.opportunity, capturedDate: meta.capturedDate, leadSource: meta.leadSource })
      const rec = { ...base, ...(edits[i] || {}) }
      const issues = validateRecord(rec)
      const email = (rec['Email'] || '').toLowerCase()
      let duplicate = false
      if (email && email.includes('@')) {
        if (existingEmails.has(email) || seen.has(email)) duplicate = true
        seen.add(email)
      }
      const isExcluded = excluded.has(i)
      const hasError = issues.some((x) => x.level === 'error')
      const status: RowStatus = isExcluded ? 'excluded' : hasError ? 'error' : duplicate ? 'duplicate' : issues.length ? 'warn' : 'valid'
      return { i, rec, issues, duplicate, status }
    })
    const counts = {
      valid: rows.filter((r) => r.status === 'valid').length,
      flagged: rows.filter((r) => r.status === 'error' || r.status === 'warn').length,
      duplicate: rows.filter((r) => r.status === 'duplicate').length,
      excluded: rows.filter((r) => r.status === 'excluded').length,
      // EVERY non-excluded row imports — missing-required rows come in flagged,
      // never silently dropped.
      importable: rows.filter((r) => r.status !== 'excluded').length,
    }
    return { rows, counts }
  }, [parsed, mapping, meta, edits, excluded, existingEmails])

  const setEdit = (i: number, fieldName: string, value: string) => setEdits((e) => ({ ...e, [i]: { ...(e[i] || {}), [fieldName]: value } }))
  const toggleExclude = (i: number) => setExcluded((s) => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n })

  const [committing, setCommitting] = useState(false)
  const [result, setResult] = useState<LeadImportResult | null>(null)
  const doCommit = async () => {
    // Every non-excluded row imports (missing-required rows land flagged).
    const records = review.rows.filter((r) => r.status !== 'excluded').map((r) => r.rec)
    if (!records.length) return crmToast('Nothing to import — include some rows', 'error')
    setCommitting(true)
    try {
      const r = await commitLeadImport({ opportunity: meta.opportunity.trim(), owner: meta.owner || undefined, capturedDate: meta.capturedDate || undefined, leadSource: meta.leadSource || undefined, records })
      setResult(r)
      crmToast(`Imported ${r.created + r.updated} lead${r.created + r.updated === 1 ? '' : 's'}`, 'success')
    } catch (e) {
      crmToast((e as Error).message || 'Import failed', 'error')
    } finally {
      setCommitting(false)
    }
  }

  const runParse = async (f: File, sheet?: string) => {
    setBusy(true)
    try {
      const p = await parseLeadImport(f, sheet)
      setParsed(p)
      setMapping(autoMapHeaders(p.headers))
      setMeta((m) => ({ ...m, opportunity: m.opportunity || (p.sheet && !/^sheet\d*$|^leads$/i.test(p.sheet) ? p.sheet : fileBase(p.filename)) }))
      if (!p.headers.length) crmToast('No columns found on that sheet', 'error')
    } catch (e) {
      crmToast((e as Error).message || 'Could not read the file', 'error')
    } finally {
      setBusy(false)
    }
  }

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    setFile(f)
    setParsed(null)
    setStep(0)
    runParse(f)
  }

  const onMapJson = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f || !parsed) return
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const json = JSON.parse(String(reader.result))
        setMapping(applyMappingJson(parsed.headers, json))
        if (json.opportunity && !meta.opportunity) setMeta((m) => ({ ...m, opportunity: json.opportunity }))
        crmToast('Mapping loaded from mapping.json', 'success')
      } catch {
        crmToast('That file is not a valid mapping.json', 'error')
      }
    }
    reader.readAsText(f)
  }

  const mappedFields = new Set(Object.values(mapping))
  const hasFullName = mappedFields.has('Full Name') || mappedFields.has(FIRSTNAME) || mappedFields.has(LASTNAME)
  const requiredStatus = REQUIRED_FIELDS.map((f) => ({ field: f, ok: mappedFields.has(f) }))
  const notesCount = Object.values(mapping).filter((v) => v === TO_NOTES).length

  return (
    <div className="h-full flex flex-col bg-surface-secondary">
      <CrmToaster />

      {/* Header */}
      <div className="px-6 pt-6 pb-4 bg-surface border-b border-border flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate('/crm/leads')} className="text-txt-tertiary hover:text-txt-primary text-sm shrink-0">← Leads</button>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-txt-primary">Import leads</h1>
            <p className="text-sm text-txt-secondary">Upload a trade-show / event sheet, map its columns, review, and import as Cold Leads</p>
          </div>
        </div>
        <Stepper step={step} />
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-6">
        <div className="max-w-4xl mx-auto space-y-5">
          {fromSkill && step === 0 && (
            <div className="rounded-xl border border-brand-300 bg-brand-50/70 dark:bg-brand-900/10 p-4 flex items-start gap-3">
              <span className="text-xl leading-none text-brand-600">⇄</span>
              <div className="text-sm">
                <p className="font-semibold text-txt-primary">Handed off from the WeQuote Lead Import skill</p>
                <p className="text-txt-secondary mt-0.5">
                  Upload the <span className="font-medium">*-unified.xlsx</span> the skill produced{meta.opportunity ? <> for <span className="font-medium">“{meta.opportunity}”</span></> : ''}, then on the mapping step click <span className="font-medium">Load mapping.json</span> to apply the skill's column mapping. You can also just upload a raw sheet.
                </p>
              </div>
            </div>
          )}

          {/* ── STEP 0: upload + details ── */}
          {step === 0 && !parsed && (
            <div onClick={() => inputRef.current?.click()} className="rounded-xl border-2 border-dashed border-border bg-surface p-12 text-center cursor-pointer hover:border-brand-300 transition-colors">
              <input ref={inputRef} type="file" accept=".csv,.tsv,.xlsx,.xls" className="hidden" onChange={onFile} />
              
              <p className="text-sm font-medium text-txt-primary">{busy ? 'Reading…' : 'Click to upload a .xlsx or .csv'}</p>
              <p className="text-xs text-txt-tertiary mt-1">Any layout — we detect the header row and let you map columns next. One sheet = one Show/Event.</p>
            </div>
          )}

          {step === 0 && parsed && (
            <>
              <div className="rounded-xl bg-surface border border-border p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-txt-primary truncate"><IconNote className="w-3.5 h-3.5 inline -mt-0.5" /> {parsed.filename}</p>
                    <p className="text-xs text-txt-tertiary mt-0.5">{parsed.rowCount} row{parsed.rowCount === 1 ? '' : 's'} · {parsed.headers.length} columns · header detected on row {parsed.headerRowNumber}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setParsed(null); setFile(null) }}>Change file</Button>
                </div>
                {parsed.sheets.length > 1 && (
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <span className="text-xs text-txt-secondary">Sheet:</span>
                    {parsed.sheets.map((s) => (
                      <button key={s} onClick={() => file && runParse(file, s)} className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${s === parsed.sheet ? 'bg-brand-50 border-brand-300 text-brand-700' : 'border-border text-txt-secondary hover:bg-surface-hover'}`}>{s}</button>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-xl bg-surface border border-border p-4">
                <p className="text-sm font-semibold text-txt-primary mb-1">Import details</p>
                <p className="text-xs text-txt-tertiary mb-3">Applied to every lead in this file. One sheet = one Show/Event.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-txt-secondary">Show / Event <span className="text-red-500">*</span></label>
                    <input className={field + ' mt-1'} value={meta.opportunity} onChange={(e) => setMeta({ ...meta, opportunity: e.target.value })} placeholder="e.g. Cedia Expo 2025" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-txt-secondary">Lead Source</label>
                    <input className={field + ' mt-1'} value={meta.leadSource} onChange={(e) => setMeta({ ...meta, leadSource: e.target.value })} placeholder="Trade Show" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-txt-secondary">Captured Date</label>
                    <input className={field + ' mt-1'} value={meta.capturedDate} onChange={(e) => setMeta({ ...meta, capturedDate: e.target.value })} placeholder="e.g. 09/10/2025" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-txt-secondary">Lead Owner</label>
                    <input className={field + ' mt-1'} value={meta.owner} onChange={(e) => setMeta({ ...meta, owner: e.target.value })} placeholder="e.g. Andy Morris" />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-xs text-txt-tertiary">Nothing is imported until you confirm at the final step.</p>
                <Button onClick={() => { if (!meta.opportunity.trim()) return crmToast('Name the Show / Event first', 'error'); setStep(1) }}>Continue to mapping →</Button>
              </div>
            </>
          )}

          {/* ── STEP 1: mapping ── */}
          {step === 1 && parsed && (
            <>
              <div className="rounded-xl bg-surface border border-border p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-semibold text-txt-primary">Map columns → unified fields</p>
                    <p className="text-xs text-txt-tertiary mt-0.5">Auto-suggested. Anything left as “→ Notes” is kept on the lead, never discarded.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input ref={mapJsonRef} type="file" accept=".json,application/json" className="hidden" onChange={onMapJson} />
                    <Button size="sm" variant="secondary" onClick={() => mapJsonRef.current?.click()}>Load mapping.json</Button>
                    <Button size="sm" variant="secondary" onClick={() => setMapping(autoMapHeaders(parsed.headers))}>Reset auto-map</Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  {requiredStatus.map((r) => (
                    <Pill key={r.field} tone={r.ok ? 'green' : 'red'}>
                      {r.ok ? '✓' : '✗'} {r.field} {r.ok ? 'mapped' : 'not mapped'}
                    </Pill>
                  ))}
                  {hasFullName && <Pill tone="blue">Full Name ✓</Pill>}
                  <Pill tone="gray">{notesCount} → Notes</Pill>
                </div>
              </div>

              <div className="rounded-xl bg-surface border border-border overflow-hidden">
                <div className="grid grid-cols-[1fr_1fr] text-[11px] uppercase tracking-wide text-txt-tertiary bg-surface-secondary border-b border-border">
                  <div className="py-2 px-4 font-medium">Source column</div>
                  <div className="py-2 px-4 font-medium">Maps to</div>
                </div>
                <div className="divide-y divide-border">
                  {parsed.headers.map((h) => (
                    <div key={h} className="grid grid-cols-[1fr_1fr] items-center gap-3 px-4 py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-txt-primary truncate">{h}</p>
                        <p className="text-xs text-txt-tertiary truncate">e.g. {parsed.samples[h] || '—'}</p>
                      </div>
                      <MapSelect value={mapping[h] || TO_NOTES} onChange={(v) => setMapping((m) => ({ ...m, [h]: v }))} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Button variant="secondary" onClick={() => setStep(0)}>← Back</Button>
                <Button onClick={() => setStep(2)}>Continue to review →</Button>
              </div>
            </>
          )}

          {/* ── STEP 2: validation + preview ── */}
          {step === 2 && parsed && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <CountTile label="Total rows" value={review.rows.length} />
                <CountTile label="Valid" value={review.counts.valid} tone="green" />
                <CountTile label="Flagged" value={review.counts.flagged} tone="amber" />
                <CountTile label="Duplicates" value={review.counts.duplicate} tone="blue" />
                <CountTile label="Will import" value={review.counts.importable} tone="green" />
              </div>

              <div className="rounded-xl bg-surface border border-border overflow-hidden">
                <div className="px-4 pt-3 pb-2 border-b border-border flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-txt-primary">Preview &amp; fix</p>
                    <p className="text-xs text-txt-tertiary">Every row imports unless you exclude it — rows missing Email/Company come in tagged “Needs review”. Edit inline, or go back to remap. Duplicates update the existing lead.</p>
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => setStep(1)}>← Remap columns</Button>
                </div>
                <div className="max-h-[28rem] overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-surface-secondary z-10">
                      <tr className="text-left text-[11px] uppercase tracking-wide text-txt-tertiary">
                        <th className="py-2 px-3 font-medium">Status</th>
                        <th className="py-2 px-3 font-medium">Full Name</th>
                        <th className="py-2 px-3 font-medium">★ Email</th>
                        <th className="py-2 px-3 font-medium">★ Company Name</th>
                        <th className="py-2 px-3 font-medium">City / Country</th>
                        <th className="py-2 px-3 font-medium">Issues</th>
                        <th className="py-2 px-3 font-medium text-center">Exclude</th>
                      </tr>
                    </thead>
                    <tbody>
                      {review.rows.map((r) => {
                        const sm = STATUS_META[r.status]
                        const dim = r.status === 'excluded' ? 'opacity-50' : ''
                        return (
                          <tr key={r.i} className={`border-t border-border align-top ${dim}`}>
                            <td className="py-2 px-3"><Pill tone={sm.tone}>{sm.label}</Pill></td>
                            <td className="py-2 px-3 text-txt-primary whitespace-nowrap">{r.rec['Full Name'] || '—'}</td>
                            <td className="py-2 px-3">
                              <input
                                value={r.rec['Email'] || ''}
                                onChange={(e) => setEdit(r.i, 'Email', e.target.value)}
                                className={`w-48 rounded border px-2 py-1 text-xs bg-surface focus:outline-none ${r.issues.some((x) => x.msg.includes('Email')) ? 'border-red-400' : 'border-border focus:border-brand-400'}`}
                              />
                            </td>
                            <td className="py-2 px-3">
                              <input
                                value={r.rec['Company Name'] || ''}
                                onChange={(e) => setEdit(r.i, 'Company Name', e.target.value)}
                                className={`w-44 rounded border px-2 py-1 text-xs bg-surface focus:outline-none ${r.issues.some((x) => x.msg.includes('Company')) ? 'border-red-400' : 'border-border focus:border-brand-400'}`}
                              />
                            </td>
                            <td className="py-2 px-3 text-txt-tertiary whitespace-nowrap">{[r.rec['City'], r.rec['Country']].filter(Boolean).join(', ') || '—'}</td>
                            <td className="py-2 px-3">
                              {r.issues.length === 0 ? (
                                <span className="text-xs text-txt-tertiary">—</span>
                              ) : (
                                <div className="flex flex-col gap-0.5">
                                  {r.issues.map((x, k) => (
                                    <span key={k} className={`text-[11px] ${x.level === 'error' ? 'text-red-600' : 'text-amber-600'}`}>{x.msg}</span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="py-2 px-3 text-center">
                              <input type="checkbox" className="accent-brand-600" checked={excluded.has(r.i)} onChange={() => toggleExclude(r.i)} />
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Button variant="secondary" onClick={() => setStep(1)}>← Back</Button>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-txt-tertiary">{review.counts.importable} of {review.rows.length} will import into “{meta.opportunity}”</span>
                  <Button disabled={review.counts.importable === 0} onClick={() => setStep(3)}>Continue to import →</Button>
                </div>
              </div>
            </>
          )}

          {/* ── STEP 3: commit ── */}
          {step === 3 && parsed && !result && (
            <div className="rounded-xl border border-border bg-surface p-8 text-center">
              
              <p className="text-sm font-semibold text-txt-primary">Import {review.counts.importable} lead{review.counts.importable === 1 ? '' : 's'} into “{meta.opportunity}”</p>
              <p className="text-xs text-txt-tertiary mt-1 max-w-md mx-auto">
                They land in Cold Leads, tagged with this Show/Event{meta.owner ? `, owned by ${meta.owner}` : ''}. Existing leads (matched by email) are updated, not duplicated. Every column is preserved on the lead.
              </p>
              <div className="flex items-center justify-center gap-2 mt-4">
                <Button variant="secondary" size="sm" onClick={() => setStep(2)}>← Back to review</Button>
                <Button loading={committing} onClick={doCommit}>Import {review.counts.importable} leads</Button>
              </div>
            </div>
          )}

          {step === 3 && result && (
            <div className="space-y-5">
              <div className="rounded-xl border border-green-300 bg-green-50/60 dark:bg-green-900/10 p-6 text-center">
                <div className="text-4xl mb-1 text-emerald-600">✓</div>
                <p className="text-sm font-semibold text-txt-primary">
                  Import complete — {result.opportunities && result.opportunities.length > 1 ? `${result.opportunities.length} opportunities` : `“${result.opportunity}”`}
                </p>
                {result.opportunities && result.opportunities.length > 1 && (
                  <p className="text-xs text-txt-tertiary mt-1">{result.opportunities.join(' · ')}</p>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <CountTile label="Created" value={result.created} tone="green" />
                <CountTile label="Updated" value={result.updated} tone="blue" />
                <CountTile label="Flagged" value={result.flagged || 0} tone="amber" />
                <CountTile label="Skipped" value={result.skipped} tone="amber" />
              </div>
              {(result.flagged || 0) > 0 && (
                <p className="text-xs text-txt-tertiary text-center">Flagged leads (missing/'@'-less email or company) were imported and tagged “Needs review”.</p>
              )}
              <div className="flex items-center justify-center gap-2">
                <Button onClick={() => navigate('/crm/leads')}>View Leads →</Button>
                <Button variant="secondary" onClick={() => navigate('/crm/opportunities')}>View Show/Event →</Button>
                <Button variant="ghost" onClick={() => { setParsed(null); setFile(null); setResult(null); setEdits({}); setExcluded(new Set()); setStep(0) }}>Import another</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
